import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--epic-id-import"
export default class extends Controller {
    static targets = [
        "matchAttributeSelect",
        "dropzone",
        "fileInput",
        "fileInfo",
        "fileName",
        "fileSize",
        "previewSection",
        "previewSummary",
        "matchedCount",
        "unmatchedCount",
        "previewTable",
        "previewTableBody",
        "previewPagination",
        "paginationList",
        "confirmButton",
        "progressCard",
        "progressText",
        "progressPercentage",
        "progressBar",
        "processedCount",
        "totalCount",
        "updatedCount",
        "errorCount",
        "successCard",
        "successMessage",
        "errorCard",
        "errorMessage",
        "errorList",
        "downloadTemplateButton"
    ]

    connect() {
        this.selectedFile = null
        this.pollingInterval = null
        this.currentTaskId = null
        this.previewData = null
        this.currentPage = 1
        this.rowsPerPage = 100
    }

    disconnect() {
        this.stopPolling()
    }

    // =========================================================================
    // Match Attribute Selection
    // =========================================================================

    matchAttributeChanged() {
        const selectedValue = this.matchAttributeSelectTarget.value

        if (selectedValue) {
            this.enableFileUpload()
            this.showDownloadTemplateButton()
        } else {
            this.disableFileUpload()
            this.hideDownloadTemplateButton()
        }

        // Clear staged file when match attribute changes
        this.clearStagedFile()
        this.hidePreview()
        this.hideResults()
    }

    // =========================================================================
    // Template Download
    // =========================================================================

    showDownloadTemplateButton() {
        if (this.hasDownloadTemplateButtonTarget) {
            this.downloadTemplateButtonTarget.classList.remove('d-none')
        }
    }

    hideDownloadTemplateButton() {
        if (this.hasDownloadTemplateButtonTarget) {
            this.downloadTemplateButtonTarget.classList.add('d-none')
        }
    }

    downloadTemplate() {
        const matchAttribute = this.matchAttributeSelectTarget.value
        if (!matchAttribute) return

        const csvContent = `epic_id,${matchAttribute}\n`
        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `epic_id_template_${matchAttribute}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)
    }

    enableFileUpload() {
        this.dropzoneTarget.style.opacity = '1'
        this.dropzoneTarget.style.pointerEvents = 'auto'
        // Enable the browse button and file input inside dropzone
        const browseButton = this.dropzoneTarget.querySelector('button')
        if (browseButton) browseButton.disabled = false
        this.fileInputTarget.disabled = false
    }

    disableFileUpload() {
        this.dropzoneTarget.style.opacity = '0.5'
        this.dropzoneTarget.style.pointerEvents = 'none'
        // Disable the browse button and file input inside dropzone
        const browseButton = this.dropzoneTarget.querySelector('button')
        if (browseButton) browseButton.disabled = true
        this.fileInputTarget.disabled = true
    }

    clearStagedFile() {
        this.selectedFile = null
        this.fileInputTarget.value = ''
        if (this.hasFileInfoTarget) {
            this.fileInfoTarget.classList.add('d-none')
        }
    }

    // =========================================================================
    // File Upload Handlers
    // =========================================================================

    handleDragOver(event) {
        event.preventDefault()
        event.stopPropagation()
        this.dropzoneTarget.style.borderColor = '#0d6efd'
        this.dropzoneTarget.style.backgroundColor = '#e7f1ff'
    }

    handleDragLeave(event) {
        event.preventDefault()
        event.stopPropagation()
        this.dropzoneTarget.style.borderColor = '#dee2e6'
        this.dropzoneTarget.style.backgroundColor = '#f8f9fa'
    }

    handleDrop(event) {
        event.preventDefault()
        event.stopPropagation()

        this.dropzoneTarget.style.borderColor = '#dee2e6'
        this.dropzoneTarget.style.backgroundColor = '#f8f9fa'

        const files = event.dataTransfer.files
        if (files.length > 0) {
            this.handleFile(files[0])
        }
    }

    triggerFileInput(event) {
        event.preventDefault()
        if (this.hasFileInputTarget) {
            this.fileInputTarget.click()
        }
    }

    handleFileSelect(event) {
        const files = event.target.files
        if (files.length > 0) {
            this.handleFile(files[0])
        }
    }

    handleFile(file) {
        // Validate file extension
        if (!file.name.toLowerCase().endsWith('.csv')) {
            sendFailureNotification('Please select a CSV file (.csv extension required)')
            return
        }

        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024
        if (file.size > maxSize) {
            sendFailureNotification('File size exceeds 10MB limit')
            return
        }

        this.selectedFile = file

        // Display file info
        this.fileNameTarget.textContent = file.name
        this.fileSizeTarget.textContent = this.formatFileSize(file.size)
        this.fileInfoTarget.classList.remove('d-none')

        // Hide any previous results
        this.hidePreview()
        this.hideResults()

        // Automatically trigger preview
        this.requestPreview()
    }

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }

    // =========================================================================
    // Preview Request
    // =========================================================================

    async requestPreview() {
        if (!this.selectedFile) {
            sendFailureNotification('Please select a file first')
            return
        }

        const matchAttribute = this.matchAttributeSelectTarget.value
        if (!matchAttribute) {
            sendFailureNotification('Please select a match attribute first')
            return
        }

        try {
            const formData = new FormData()
            formData.append('file', this.selectedFile)
            formData.append('match_attribute', matchAttribute)

            const response = await fetch('/settings/epic-id-import/preview', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            const data = await response.json()

            if (response.ok) {
                this.previewData = data
                this.currentPage = 1
                this.renderPreview(data)
            } else {
                // Display validation errors
                if (data.errors && Array.isArray(data.errors)) {
                    this.displayErrors(data.errors)
                } else {
                    sendFailureNotification(data.error || 'Preview generation failed')
                }
            }
        } catch (error) {
            console.error('Preview error:', error)
            sendFailureNotification('Failed to generate preview. Please try again.')
        }
    }

    renderPreview(data) {
        // Show preview section
        this.previewSectionTarget.classList.remove('d-none')

        // Update summary counts
        this.matchedCountTarget.textContent = data.total_matched || 0
        this.unmatchedCountTarget.textContent = data.total_unmatched || 0

        // Combine matched and unmatched for display
        this.allPreviewRows = [
            ...(data.matched || []).map(row => ({ ...row, status: 'matched' })),
            ...(data.unmatched || []).map(row => ({ ...row, status: 'unmatched' }))
        ]

        // Render the current page
        this.renderPreviewPage()

        // Enable/disable confirm button
        if (data.total_matched > 0) {
            this.confirmButtonTarget.disabled = false
        } else {
            this.confirmButtonTarget.disabled = true
        }
    }

    renderPreviewPage() {
        const totalRows = this.allPreviewRows.length
        const totalPages = Math.ceil(totalRows / this.rowsPerPage)
        const startIndex = (this.currentPage - 1) * this.rowsPerPage
        const endIndex = Math.min(startIndex + this.rowsPerPage, totalRows)
        const pageRows = this.allPreviewRows.slice(startIndex, endIndex)

        // Clear existing rows
        this.previewTableBodyTarget.innerHTML = ''

        // Render rows
        pageRows.forEach(row => {
            const tr = document.createElement('tr')

            if (row.status === 'unmatched') {
                tr.classList.add('table-warning')
            }

            const matchValueTd = document.createElement('td')
            matchValueTd.textContent = row.match_value || ''

            const currentEpicIdTd = document.createElement('td')
            currentEpicIdTd.textContent = row.current_epic_id || '—'

            const newEpicIdTd = document.createElement('td')
            newEpicIdTd.textContent = row.new_epic_id || ''

            const statusTd = document.createElement('td')
            if (row.status === 'matched') {
                statusTd.innerHTML = '<span class="badge bg-success">Matched</span>'
            } else {
                statusTd.innerHTML = '<span class="badge bg-warning text-dark">Unmatched</span>'
            }

            tr.appendChild(matchValueTd)
            tr.appendChild(currentEpicIdTd)
            tr.appendChild(newEpicIdTd)
            tr.appendChild(statusTd)

            this.previewTableBodyTarget.appendChild(tr)
        })

        // Render pagination if needed
        if (totalPages > 1) {
            this.renderPagination(totalPages)
            this.previewPaginationTarget.classList.remove('d-none')
        } else {
            this.previewPaginationTarget.classList.add('d-none')
        }
    }

    renderPagination(totalPages) {
        this.paginationListTarget.innerHTML = ''

        // Previous button
        const prevLi = document.createElement('li')
        prevLi.className = `page-item ${this.currentPage === 1 ? 'disabled' : ''}`
        const prevLink = document.createElement('a')
        prevLink.className = 'page-link'
        prevLink.href = '#'
        prevLink.textContent = 'Previous'
        prevLink.addEventListener('click', (e) => {
            e.preventDefault()
            if (this.currentPage > 1) {
                this.currentPage--
                this.renderPreviewPage()
            }
        })
        prevLi.appendChild(prevLink)
        this.paginationListTarget.appendChild(prevLi)

        // Page number buttons (show max 5 pages around current)
        const startPage = Math.max(1, this.currentPage - 2)
        const endPage = Math.min(totalPages, startPage + 4)

        for (let i = startPage; i <= endPage; i++) {
            const pageLi = document.createElement('li')
            pageLi.className = `page-item ${i === this.currentPage ? 'active' : ''}`
            const pageLink = document.createElement('a')
            pageLink.className = 'page-link'
            pageLink.href = '#'
            pageLink.textContent = i
            pageLink.addEventListener('click', (e) => {
                e.preventDefault()
                this.currentPage = i
                this.renderPreviewPage()
            })
            pageLi.appendChild(pageLink)
            this.paginationListTarget.appendChild(pageLi)
        }

        // Next button
        const nextLi = document.createElement('li')
        nextLi.className = `page-item ${this.currentPage === totalPages ? 'disabled' : ''}`
        const nextLink = document.createElement('a')
        nextLink.className = 'page-link'
        nextLink.href = '#'
        nextLink.textContent = 'Next'
        nextLink.addEventListener('click', (e) => {
            e.preventDefault()
            if (this.currentPage < totalPages) {
                this.currentPage++
                this.renderPreviewPage()
            }
        })
        nextLi.appendChild(nextLink)
        this.paginationListTarget.appendChild(nextLi)
    }

    // =========================================================================
    // Confirm Upload
    // =========================================================================

    async confirmUpload(event) {
        event.preventDefault()

        if (!this.selectedFile) {
            sendFailureNotification('No file selected for import')
            return
        }

        const matchAttribute = this.matchAttributeSelectTarget.value
        if (!matchAttribute) {
            sendFailureNotification('Please select a match attribute first')
            return
        }

        // Disable confirm button to prevent double-click
        this.confirmButtonTarget.disabled = true
        this.confirmButtonTarget.innerHTML = '<i class="bi bi-hourglass-split me-2"></i>Starting Import...'

        try {
            const formData = new FormData()
            formData.append('file', this.selectedFile)
            formData.append('match_attribute', matchAttribute)

            const response = await fetch('/settings/epic-id-import/confirm', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            const data = await response.json()

            if (response.ok) {
                this.currentTaskId = data.task_id
                sendSuccessNotification(data.message || 'Import started successfully')

                // Hide preview, show progress
                this.hidePreview()
                this.showProgressCard(this.previewData ? this.previewData.total_matched : 0)
                this.startPolling()
            } else {
                if (data.errors && Array.isArray(data.errors)) {
                    this.displayErrors(data.errors)
                } else {
                    sendFailureNotification(data.error || 'Failed to start import')
                }
                this.resetConfirmButton()
            }
        } catch (error) {
            console.error('Confirm upload error:', error)
            sendFailureNotification('Failed to start import. Please try again.')
            this.resetConfirmButton()
        }
    }

    resetConfirmButton() {
        this.confirmButtonTarget.disabled = false
        this.confirmButtonTarget.innerHTML = '<i class="bi bi-check-circle me-2"></i>Confirm Upload'
    }

    // =========================================================================
    // Progress and Polling
    // =========================================================================

    showProgressCard(totalRows) {
        this.progressCardTarget.classList.remove('d-none')
        this.progressBarTarget.style.width = '0%'
        this.progressBarTarget.setAttribute('aria-valuenow', 0)
        this.progressPercentageTarget.textContent = '0%'
        this.progressTextTarget.textContent = 'Starting import...'
        this.processedCountTarget.textContent = '0'
        this.totalCountTarget.textContent = totalRows || '0'
        this.updatedCountTarget.textContent = '0'
        this.errorCountTarget.textContent = '0'
    }

    startPolling() {
        this.pollingInterval = setInterval(() => {
            this.checkImportStatus()
        }, 2000)
    }

    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null
        }
    }

    async checkImportStatus() {
        if (!this.currentTaskId) {
            this.stopPolling()
            return
        }

        try {
            const response = await fetch(`/settings/epic-id-import/status/${this.currentTaskId}`, {
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch status')
            }

            const data = await response.json()

            // Update progress display
            this.updateProgress(data)

            // Check if import is complete
            if (data.status === 'completed' || data.status === 'failed') {
                this.stopPolling()
                this.handleImportComplete(data)
            }
        } catch (error) {
            console.error('Status check error:', error)
            this.stopPolling()
            sendFailureNotification('Failed to check import status')
        }
    }

    updateProgress(data) {
        const percentage = data.percentage || 0

        // Update progress bar
        this.progressBarTarget.style.width = `${percentage}%`
        this.progressBarTarget.setAttribute('aria-valuenow', percentage)
        this.progressPercentageTarget.textContent = `${percentage}%`

        // Update progress text
        if (data.status === 'running') {
            this.progressTextTarget.textContent = 'Processing...'
        } else if (data.status === 'waiting') {
            this.progressTextTarget.textContent = 'Waiting to start...'
        }

        // Update counts
        this.processedCountTarget.textContent = data.processed_rows || 0
        this.totalCountTarget.textContent = data.total_rows || 0
        this.updatedCountTarget.textContent = data.updated_count || 0
        this.errorCountTarget.textContent = data.error_count || 0
    }

    handleImportComplete(data) {
        const errorCount = data.error_count || 0
        const updatedCount = data.updated_count || 0

        if (data.status === 'completed') {
            // Update progress to 100%
            this.progressBarTarget.style.width = '100%'
            this.progressBarTarget.setAttribute('aria-valuenow', 100)
            this.progressPercentageTarget.textContent = '100%'
            this.progressTextTarget.textContent = 'Complete!'

            if (errorCount > 0) {
                // Partial success
                this.successMessageTarget.textContent =
                    `Import complete. Updated ${updatedCount} devices with ${errorCount} errors.`
                this.successCardTarget.classList.remove('d-none')
                this.displayImportErrors(data.errors)
                sendSuccessNotification(`Import completed with ${errorCount} errors. Check details below.`)
            } else {
                // Full success
                this.successMessageTarget.textContent =
                    `Import complete! Successfully updated ${updatedCount} devices.`
                this.successCardTarget.classList.remove('d-none')
                sendSuccessNotification('Import completed successfully!')
            }
        } else if (data.status === 'failed') {
            // Complete failure
            this.progressTextTarget.textContent = 'Failed'
            this.progressBarTarget.classList.remove('progress-bar-animated')
            this.progressBarTarget.classList.add('bg-danger')

            this.errorMessageTarget.textContent = data.error_message || 'Import failed due to an unexpected error.'
            this.errorCardTarget.classList.remove('d-none')
            sendFailureNotification(data.error_message || 'Import failed. Please review errors and try again.')

            if (data.errors && data.errors.length > 0) {
                this.displayImportErrors(data.errors)
            }
        }

        // Reset state for next import
        this.selectedFile = null
        this.fileInputTarget.value = ''
        this.fileInfoTarget.classList.add('d-none')
    }

    // =========================================================================
    // Error Display
    // =========================================================================

    displayErrors(errors) {
        this.errorMessageTarget.textContent = 'Validation errors occurred:'
        this.errorListTarget.innerHTML = ''

        // Limit display to 100 errors
        const displayErrors = errors.slice(0, 100)

        displayErrors.forEach((error, index) => {
            const errorItem = document.createElement('div')
            errorItem.className = 'accordion-item'

            const errorHeader = document.createElement('h2')
            errorHeader.className = 'accordion-header'
            errorHeader.id = `errorHeading${index}`

            const errorButton = document.createElement('button')
            errorButton.className = 'accordion-button collapsed'
            errorButton.type = 'button'
            errorButton.setAttribute('data-bs-toggle', 'collapse')
            errorButton.setAttribute('data-bs-target', `#errorCollapse${index}`)
            errorButton.setAttribute('aria-expanded', 'false')
            errorButton.setAttribute('aria-controls', `errorCollapse${index}`)

            if (typeof error === 'string') {
                errorButton.textContent = error
            } else if (error.row) {
                errorButton.textContent = `Row ${error.row}: ${error.message || 'Error'}`
            } else {
                errorButton.textContent = error.message || 'Validation error'
            }

            errorHeader.appendChild(errorButton)

            const errorCollapse = document.createElement('div')
            errorCollapse.id = `errorCollapse${index}`
            errorCollapse.className = 'accordion-collapse collapse'
            errorCollapse.setAttribute('aria-labelledby', `errorHeading${index}`)
            errorCollapse.setAttribute('data-bs-parent', '#errorAccordion')

            const errorBody = document.createElement('div')
            errorBody.className = 'accordion-body'

            if (typeof error === 'string') {
                errorBody.textContent = error
            } else {
                let errorDetails = '<ul class="mb-0">'
                if (error.row) errorDetails += `<li><strong>Row:</strong> ${error.row}</li>`
                if (error.message) errorDetails += `<li><strong>Error:</strong> ${error.message}</li>`
                errorDetails += '</ul>'
                errorBody.innerHTML = errorDetails
            }

            errorCollapse.appendChild(errorBody)
            errorItem.appendChild(errorHeader)
            errorItem.appendChild(errorCollapse)
            this.errorListTarget.appendChild(errorItem)
        })

        this.errorCardTarget.classList.remove('d-none')
    }

    displayImportErrors(errors) {
        if (!errors || errors.length === 0) return

        this.errorMessageTarget.textContent = `${errors.length} row(s) encountered errors during processing:`
        this.errorListTarget.innerHTML = ''

        // Limit display to 100 errors per Req 6 AC 5
        const displayErrors = errors.slice(0, 100)

        displayErrors.forEach((error, index) => {
            const errorItem = document.createElement('div')
            errorItem.className = 'accordion-item'

            const errorHeader = document.createElement('h2')
            errorHeader.className = 'accordion-header'
            errorHeader.id = `importErrorHeading${index}`

            const errorButton = document.createElement('button')
            errorButton.className = 'accordion-button collapsed'
            errorButton.type = 'button'
            errorButton.setAttribute('data-bs-toggle', 'collapse')
            errorButton.setAttribute('data-bs-target', `#importErrorCollapse${index}`)
            errorButton.setAttribute('aria-expanded', 'false')
            errorButton.setAttribute('aria-controls', `importErrorCollapse${index}`)

            if (error.row) {
                errorButton.textContent = `Row ${error.row}: ${error.message || 'Processing error'}`
            } else {
                errorButton.textContent = error.message || 'Processing error'
            }

            errorHeader.appendChild(errorButton)

            const errorCollapse = document.createElement('div')
            errorCollapse.id = `importErrorCollapse${index}`
            errorCollapse.className = 'accordion-collapse collapse'
            errorCollapse.setAttribute('aria-labelledby', `importErrorHeading${index}`)
            errorCollapse.setAttribute('data-bs-parent', '#errorAccordion')

            const errorBody = document.createElement('div')
            errorBody.className = 'accordion-body'
            errorBody.textContent = error.message || 'An error occurred while processing this row.'

            errorCollapse.appendChild(errorBody)
            errorItem.appendChild(errorHeader)
            errorItem.appendChild(errorCollapse)
            this.errorListTarget.appendChild(errorItem)
        })

        this.errorCardTarget.classList.remove('d-none')
    }

    // =========================================================================
    // UI Helpers
    // =========================================================================

    hidePreview() {
        if (this.hasPreviewSectionTarget) {
            this.previewSectionTarget.classList.add('d-none')
        }
    }

    hideResults() {
        if (this.hasProgressCardTarget) this.progressCardTarget.classList.add('d-none')
        if (this.hasErrorCardTarget) this.errorCardTarget.classList.add('d-none')
        if (this.hasSuccessCardTarget) this.successCardTarget.classList.add('d-none')
    }
}
